/**
 * inspectionSync — the offline brain for technician inspections.
 *
 * Flow
 *   1. Online, on the Tech tab: `precacheBundle()` pulls the working set
 *      (own cards + team day + WOs + attached inspections + the whole
 *      template library) into IndexedDB.
 *   2. Offline: the conduct screen reads from that cache, writes every
 *      answer into a local draft, and stores photo blobs locally
 *      (referenced as "local://<id>" inside the draft).
 *   3. "Complete" while offline marks the draft `completed_pending`.
 *   4. Back online: `flush()` uploads each local photo, swaps the
 *      local:// refs for real URLs, then POSTs the batch to
 *      /api/tech/inspections/sync. A draft is deleted ONLY after the
 *      server acknowledges that exact record.
 *
 * Auto-flush triggers: the `online` event, a 30s ticker while anything
 * is pending, and app boot. The tech never has to remember to sync;
 * "Sync now" is there for reassurance.
 */
import { getOfflineBundle, syncTechInspections, uploadInspectionPhoto } from "@/lib/api";
import {
  allDrafts, deleteDraft, deletePhoto, getBundle, getDraft, photosForInspection,
  putBundle, putDraft, putPhoto, requestPersistence, setMeta, getMeta,
} from "@/lib/offlineDB";

export const LOCAL_PREFIX = "local://";

const listeners = new Set();
let ticker = null;
let flushing = false;

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function emit() {
  listeners.forEach((fn) => { try { fn(); } catch { /* ignore */ } });
}

export function isOnline() {
  return typeof navigator === "undefined" ? true : navigator.onLine !== false;
}

/* ── Pre-cache ───────────────────────────────────────────────────── */
export async function precacheBundle(days = 14) {
  if (!isOnline()) return null;
  try {
    const res = await getOfflineBundle(days);
    await putBundle(res.data);
    await setMeta("last_precache", new Date().toISOString());
    requestPersistence();
    emit();
    return res.data;
  } catch {
    return null;
  }
}
export const cachedBundle = getBundle;
export async function lastPrecacheAt() { return await getMeta("last_precache"); }

export async function cachedTemplates() {
  const b = await getBundle();
  return b?.templates || [];
}
export async function cachedInspection(id) {
  const b = await getBundle();
  return (b?.inspections || []).find((i) => i.id === id) || null;
}

/* ── Drafts ──────────────────────────────────────────────────────── */
export async function saveDraft(inspection, { state } = {}) {
  if (!inspection?.id) return;
  const existing = await getDraft(inspection.id);
  await putDraft({
    id: inspection.id,
    client_uuid: existing?.client_uuid || crypto.randomUUID(),
    server_id: inspection.id,
    state: state || existing?.state || "in_progress",
    offline_completed_at: state === "completed_pending"
      ? (existing?.offline_completed_at || new Date().toISOString())
      : existing?.offline_completed_at || null,
    payload: inspection,
  });
  emit();
}

export async function markCompletedPending(inspection) {
  await saveDraft(inspection, { state: "completed_pending" });
  scheduleFlush();
}

export async function draftFor(id) { return await getDraft(id); }

export async function clearDraft(id) {
  await deleteDraft(id);
  emit();
}

/** Drafts still waiting on the server. */
export async function pendingDrafts() {
  const drafts = await allDrafts();
  return drafts.filter((d) => d.state === "completed_pending");
}
export async function pendingCount() {
  return (await pendingDrafts()).length;
}

/** Per-inspection UI state: not_started | in_progress | pending_sync | synced */
export async function inspectionState(inspection) {
  const draft = await getDraft(inspection.id);
  if (draft?.state === "completed_pending") return "pending_sync";
  if (inspection.status === "complete") return "synced";
  const answered = (inspection.locations || []).some((l) =>
    (l.responses || []).some((r) => r.value)
  );
  return answered ? "in_progress" : "not_started";
}

/* ── Photos ──────────────────────────────────────────────────────── */
export async function storePhotoLocally(inspectionId, file) {
  const photoId = crypto.randomUUID();
  await putPhoto({
    photo_id: photoId,
    inspection_id: inspectionId,
    blob: file,
    name: file.name || `${photoId}.jpg`,
    created_at: new Date().toISOString(),
  });
  emit();
  return `${LOCAL_PREFIX}${photoId}`;
}

/** Object URLs for every locally-stored photo of an inspection, so the
 *  conduct screen can render thumbnails with no network. */
export async function localPhotoUrls(inspectionId) {
  const photos = await photosForInspection(inspectionId);
  const map = {};
  photos.forEach((p) => {
    try { map[`${LOCAL_PREFIX}${p.photo_id}`] = URL.createObjectURL(p.blob); } catch { /* ignore */ }
  });
  return map;
}

/* ── Sync ────────────────────────────────────────────────────────── */
async function uploadLocalPhotos(inspectionId) {
  const photos = await photosForInspection(inspectionId);
  const replacements = {};
  for (const p of photos) {
    try {
      const file = p.blob instanceof File
        ? p.blob
        : new File([p.blob], p.name || "photo.jpg", { type: p.blob.type || "image/jpeg" });
      const res = await uploadInspectionPhoto(file);
      replacements[`${LOCAL_PREFIX}${p.photo_id}`] = res.data.url;
      await deletePhoto(p.photo_id);
    } catch {
      // Leave the blob in place — the next flush retries it.
    }
  }
  return replacements;
}

function swapPhotoRefs(payload, replacements) {
  const swap = (url) => replacements[url] || url;
  const locations = (payload.locations || []).map((loc) => ({
    ...loc,
    overall_photos: (loc.overall_photos || []).map(swap),
    responses: (loc.responses || []).map((r) => ({
      ...r,
      photos: (r.photos || []).map(swap),
    })),
  }));
  return { ...payload, locations };
}

function stillHasLocalRefs(payload) {
  return (payload.locations || []).some((loc) =>
    (loc.overall_photos || []).some((u) => String(u).startsWith(LOCAL_PREFIX)) ||
    (loc.responses || []).some((r) =>
      (r.photos || []).some((u) => String(u).startsWith(LOCAL_PREFIX))
    )
  );
}

export async function flush({ manual = false } = {}) {
  if (flushing) return { skipped: "in_flight" };
  if (!isOnline()) return { skipped: "offline" };
  const drafts = await pendingDrafts();
  if (!drafts.length) return { skipped: "empty" };

  flushing = true;
  const items = [];
  const prepared = [];
  try {
    for (const draft of drafts) {
      const replacements = await uploadLocalPhotos(draft.id);
      let payload = swapPhotoRefs(draft.payload, replacements);
      if (stillHasLocalRefs(payload)) {
        // A photo upload failed — keep the whole record local and retry
        // later rather than syncing an inspection with broken images.
        await putDraft({ ...draft, payload, last_error: "photo upload pending" });
        continue;
      }
      await putDraft({ ...draft, payload });
      prepared.push(draft);
      items.push({
        client_uuid: draft.client_uuid,
        id: draft.server_id || draft.id,
        payload,
        completed: true,
        offline_completed_at: draft.offline_completed_at,
      });
    }
    if (!items.length) return { skipped: "photos_pending" };

    const res = await syncTechInspections(items);
    const results = res.data?.results || [];
    const acked = new Set(
      results
        .filter((r) => ["created", "updated", "already_synced", "conflict_kept_both"].includes(r.status))
        .map((r) => r.client_uuid)
    );
    for (const draft of prepared) {
      if (acked.has(draft.client_uuid)) await deleteDraft(draft.id);
    }
    await setMeta("last_sync", new Date().toISOString());
    emit();
    return { synced: acked.size, results, manual };
  } catch (e) {
    // Nothing is deleted on failure — the queue survives for the next try.
    return { error: e?.message || "sync failed" };
  } finally {
    flushing = false;
    emit();
  }
}

export async function lastSyncAt() { return await getMeta("last_sync"); }

/* ── Auto-flush wiring ───────────────────────────────────────────── */
export function scheduleFlush() {
  if (ticker) return;
  ticker = setInterval(async () => {
    const count = await pendingCount();
    if (!count) { clearInterval(ticker); ticker = null; return; }
    if (isOnline()) await flush();
  }, 30000);
}

let wired = false;
export function initInspectionSync() {
  if (wired || typeof window === "undefined") return;
  wired = true;
  window.addEventListener("online", () => { flush(); emit(); });
  window.addEventListener("offline", () => emit());
  // App boot: drain anything left over from the last session.
  flush();
  scheduleFlush();
}
